// WHAT THE MODEL IS ASKED ABOUT A CLIENT'S SLACK MESSAGES, AND WHAT AN ANSWER
// MUST BE.
//
// ⭐ PURE. What is left of the retired triage's half is a wait's input hash
// (`triageInputText`, which every wait's key still carries) and the messages
// as words (`plainText`). Job 2 (action items, items.v2) is the rest of this
// file: a conversation in, a prompt out; an answer in, new items and
// suggestions out.
//
// The SYSTEM prompts are not here any more: each is a skill file under
// skills/client-slack-*/ (lib/client-slack-skills.ts), so the page that shows
// it and the call that sends it read the same words. What stays is the user
// turn each call builds, the answer's gate, and the input hashes, whose version
// is the skill registry's.
//
// 🚨 NO REAL CLIENT MESSAGE GOES IN THIS FILE OR ITS TESTS. Every example here
// is invented.
//
// Erasable TypeScript: the sidecar's tick (tools/client-slack-sync.ts) imports
// this under node's type stripping.

import { ACTION_ITEM_STATUSES, isActionItemStatus, type ActionItemStatus, type Owes } from "@/lib/action-items";
import { spoken, type Wait, type WaitMessage, type WaitPerson } from "@/lib/client-slack-waits";
import { activeSkillVersion } from "@/skills/skills";

/**
 * The retired triage's version (`triage.v1`, frozen in skills/skills.ts),
 * still the first line of every wait's input hash: a person's mark and the
 * respond skill's read are kept against that hash, so changing it would
 * orphan every one of them.
 */
export const TRIAGE_PROMPT_VERSION = activeSkillVersion("client_slack_triage");

/** A message as the model's window needs it: the rule's fields, and its files. */
export interface PromptMessage extends WaitMessage {
  files?: ReadonlyArray<{ name: string; type?: string }> | null;
}

/** A Slack person, by id, as the prompt names them. A NULL name is Slack's "no such user". */
export interface PromptPerson extends WaitPerson {
  name: string | null;
}

/** One message's words, at most. */
const TEXT_MAX = 1_500;

/**
 * What a wait's reads and marks are kept against: the triage's version and the
 * waiting messages' ts and words, in order. The server hashes it (`input_hash`). The
 * context is NOT in it, on purpose: a reply arriving after the wait, a name
 * Slack fills in later, or an old message falling out of the 90-day read must
 * not buy a second call for a verdict about the same words.
 */
export function triageInputText(wait: Pick<Wait, "anchors">, messages: readonly Pick<WaitMessage, "ts" | "text">[]): string {
  const byTs = new Map(messages.map((m) => [m.ts, m]));
  return [TRIAGE_PROMPT_VERSION, ...wait.anchors.map((ts) => `${ts}\t${byTs.get(ts)?.text ?? ""}`)].join("\n");
}

const TZ = "America/New_York";
const when = new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Slack's mrkdwn, as words a model reads: a mention as @Name, a link as its label and address. */
export function plainText(text: string, people: ReadonlyMap<string, PromptPerson>): string {
  const out = text
    .replace(/<@(\w+)(?:\|[^>]*)?>/g, (_, u: string) => `@${people.get(u)?.name ?? "someone"}`)
    .replace(/<#\w+\|([^>]*)>/g, (_, name: string) => `#${name}`)
    .replace(/<!(here|channel|everyone)[^>]*>/g, (_, w: string) => `@${w}`)
    .replace(/<(https?:[^|>]+)\|([^>]+)>/g, (_, url: string, label: string) => `${label} (${url})`)
    .replace(/<(https?:[^>]+)>/g, (_, url: string) => url)
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
  return out.length > TEXT_MAX ? `${out.slice(0, TEXT_MAX)} …(cut)` : out;
}

function line(m: PromptMessage, people: ReadonlyMap<string, PromptPerson>, waiting: boolean): string {
  const p = m.userId === null ? undefined : people.get(m.userId);
  const who = `${p?.name ?? "Unknown person"}${p?.yws ? " [YWS]" : ""}`;
  const files = (m.files ?? []).map((f) => ` [file: ${f.name || "unnamed"}]`).join("");
  const ours = waiting
    ? (m.reactions ?? []).filter((r) => (r.users ?? []).some((u) => people.get(u)?.yws)).map((r) => `:${r.name}:`)
    : [];
  const reacted = ours.length > 0 ? ` (YWS reacted ${ours.join(" ")})` : "";
  return `[${when.format(new Date(Number(m.ts) * 1000))}] ${who}: ${plainText(m.text, people)}${files}${reacted}`;
}

/** An answer items.v2's gate refused, and which rule refused it (never the answer's words). */
export class TriageRefused extends Error {
  constructor(why: string) {
    super(why);
    this.name = "TriageRefused";
  }
}

// ═══ Job 2: action items, per conversation ══════════════════════════════════
//
// WHAT THE MODEL READS for one conversation: a thread (its first message and
// its latest replies), or a message on its own with the channel just before
// and after it; the brand's open action items, numbered, so it can suggest a
// status on one rather than open it twice; and it answers with new items and
// suggestions. The assignee is NOT the model's: it is the YWS person who
// last spoke in the thread, else the brand's lead AM, and that is a rule
// (lib/server/client-slack-items.ts), not a guess.
//
// ⚠️ ITS OWN VERSION, NOT TRIAGE_PROMPT_VERSION, which keys every wait and
// must not move.

/**
 * items.v2's version, in every conversation's input hash it made. ⚠️ No longer
 * the registry's active version: the action items moved to
 * items.v3 (the ledger, lib/client-slack-items-v3.ts and lib/server/client-
 * slack-items.ts). Everything below in this section is items.v2, kept for the
 * eval's reading of what prod decided (tools/client-slack-eval-prod.ts) and for
 * items.v2's receipts; nothing in the tick calls it.
 */
export const ITEMS_PROMPT_VERSION = "items.v2";

/** A thread's messages the model sees: its first, then the latest this many. */
const ITEMS_THREAD_LATEST = 30;
/** Around a message on its own: the channel's top-level messages before and after it. */
const ITEMS_AROUND = 5;
/** At most this many new items from one read; more is a model listing every sentence. */
export const ITEMS_NEW_MAX = 5;

/**
 * One conversation the model reads for action items: a thread, or a message
 * on its own. `threadTs` is its key, as a wait's (`Wait.threadTs`): the root's
 * ts, or the lone message's own, which stays its key if it is later replied to.
 */
export interface ItemsConversation {
  channelId: string;
  threadTs: string;
  /** A message on its own in the channel: no replies. */
  lone: boolean;
  /** Its own messages, people only (`spoken`), oldest first. */
  messages: PromptMessage[];
}

/**
 * Every conversation in one channel worth a read: people only, at least one
 * message from the client (an unknown person counts as the client, as in the
 * wait rule), and a message at or after `activeSince` (seconds). Newest
 * activity first.
 */
export function itemConversations(
  channelId: string,
  messages: readonly PromptMessage[],
  people: ReadonlyMap<string, WaitPerson>,
  activeSince: number,
): ItemsConversation[] {
  const by = new Map<string, PromptMessage[]>();
  for (const m of messages) {
    if (!spoken(m, people)) continue;
    const key = m.threadTs ?? m.ts;
    by.set(key, [...(by.get(key) ?? []), m]);
  }
  const out: Array<ItemsConversation & { latest: number }> = [];
  by.forEach((ms, key) => {
    ms.sort((a, b) => Number(a.ts) - Number(b.ts));
    const latest = Number(ms[ms.length - 1].ts);
    if (latest < activeSince) return;
    if (!ms.some((m) => !(people.get(m.userId as string)?.yws ?? false))) return;
    out.push({ channelId, threadTs: key, lone: ms.length === 1 && ms[0].threadTs === null, messages: ms, latest });
  });
  return out.sort((a, b) => b.latest - a.latest).map(({ latest: _, ...c }) => c);
}

/**
 * What a conversation's read is kept against: the prompt's version and its own
 * messages' ts and words. Not the channel around a lone message, and not the
 * items listed: a later message beside it, or a person editing an item, buys no
 * second read.
 */
export function itemsInputText(c: Pick<ItemsConversation, "messages">): string {
  return [ITEMS_PROMPT_VERSION, ...c.messages.map((m) => `${m.ts}\t${m.text}`)].join("\n");
}

export interface ItemsWindow {
  /** A message on its own, with the channel around it; else a thread. */
  lone: boolean;
  /** The channel just before a lone message (empty for a thread). */
  before: PromptMessage[];
  /** The conversation's messages the model sees, oldest first. */
  shown: PromptMessage[];
  /** A thread's replies between its first and the latest shown, left out. */
  skipped: number;
  /** The channel just after a lone message (empty for a thread). */
  after: PromptMessage[];
}

/** The messages the model sees for one conversation, out of its channel's stored messages (any order). */
export function itemsWindow(c: ItemsConversation, channel: readonly PromptMessage[], people: ReadonlyMap<string, WaitPerson>): ItemsWindow {
  if (!c.lone) {
    const latest = c.messages.slice(1).slice(-ITEMS_THREAD_LATEST);
    return { lone: false, before: [], shown: [c.messages[0], ...latest], skipped: c.messages.length - 1 - latest.length, after: [] };
  }
  const at = Number(c.threadTs);
  const top = channel
    .filter((m) => spoken(m, people) && (m.threadTs === null || m.threadTs === m.ts) && m.ts !== c.threadTs)
    .sort((a, b) => Number(a.ts) - Number(b.ts));
  return {
    lone: true,
    before: top.filter((m) => Number(m.ts) < at).slice(-ITEMS_AROUND),
    shown: c.messages,
    skipped: 0,
    after: top.filter((m) => Number(m.ts) > at).slice(0, ITEMS_AROUND),
  };
}

/** An item the model is told about, by its prompt id (`i1`, …). */
export interface PromptItem {
  ref: string;
  title: string;
  owes: Owes;
  /** A person's status, or null. */
  status: ActionItemStatus | null;
  suggestedStatus: ActionItemStatus | null;
  /** Linked to a message of this conversation. */
  linked: boolean;
}

// THE SYSTEM PROMPT IS A FILE: skills/client-slack-items/items.v2.md, read by
// lib/server/client-slack-skill.ts and rendered on the Action Item Tracker
// skill page. Moved out of this file word for word.

/** The JSON shape, as the client's validator checks it. `acceptItems` is the stricter gate. */
export const ITEMS_SCHEMA: Record<string, unknown> = {
  type: "object",
  additionalProperties: false,
  required: ["new_items", "updates"],
  properties: {
    new_items: {
      type: "array",
      maxItems: 10,
      items: {
        type: "object",
        required: ["title", "description", "owes", "status", "why", "escalation", "messages"],
        properties: {
          title: { type: "string" },
          description: { type: "string" },
          owes: { type: "string", enum: ["us", "them"] },
          status: { type: "string", enum: [...ACTION_ITEM_STATUSES] },
          why: { type: "string" },
          escalation: { type: "boolean" },
          messages: { type: "array", items: { type: "string" } },
        },
      },
    },
    updates: {
      type: "array",
      items: {
        type: "object",
        required: ["item", "status", "why", "escalation", "messages"],
        properties: {
          item: { type: "string" },
          status: { type: "string", enum: [...ACTION_ITEM_STATUSES] },
          why: { type: "string" },
          escalation: { type: "boolean" },
          messages: { type: "array", items: { type: "string" } },
        },
      },
    },
  },
};

/**
 * The status words items v2's prompt was written and measured with, frozen: the
 * page's own (`STATUS_WORDS`) moved on ("To do"), and a model's input must not
 * move with a label.
 */
const PROMPT_STATUS_WORDS: Record<ActionItemStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  done: "Done",
  dropped: "Dropped",
};

const itemLine = (i: PromptItem): string =>
  `[${i.ref}] ${i.owes === "us" ? "We owe" : "Client owes"}: ${i.title} (status: ${
    i.status !== null ? `${PROMPT_STATUS_WORDS[i.status]}, set by YWS: don't suggest a status` : `${PROMPT_STATUS_WORDS[i.suggestedStatus ?? "not_started"]}, suggested`
  }${i.linked ? "; from this conversation" : ""})`;

/**
 * The user turn for one conversation, and the ids the answer may cite: `m1`… for
 * each message shown, oldest first, and the items' own refs.
 */
export function itemsUserPrompt(o: {
  brand: string;
  channel: string;
  window: ItemsWindow;
  items: readonly PromptItem[];
  people: ReadonlyMap<string, PromptPerson>;
}): { text: string; messages: Map<string, PromptMessage>; own: Set<string> } {
  const w = o.window;
  const refs = new Map<string, PromptMessage>();
  const own = new Set<string>();
  const say = (m: PromptMessage, mine = false): string => {
    const ref = `m${refs.size + 1}`;
    refs.set(ref, m);
    if (mine) own.add(ref);
    return `[${ref}] ${line(m, o.people, false)}`;
  };
  const parts = [`Brand: ${o.brand}. Slack channel: #${o.channel}. People at YWS are marked [YWS]; everyone else is the client. Times are New York's.`, ""];
  parts.push(o.items.length > 0 ? "The brand's open action items:" : "The brand has no open action items.");
  for (const i of o.items) parts.push(itemLine(i));
  parts.push("");
  if (w.before.length > 0) {
    parts.push("The channel just before the message:");
    for (const m of w.before) parts.push(say(m));
    parts.push("");
  }
  if (w.lone) {
    parts.push("The message, on its own in the channel:");
  } else {
    parts.push(w.skipped > 0 ? `The thread (its first message, then the latest ${w.shown.length - 1}; ${w.skipped} in between not shown):` : "The thread:");
  }
  for (const m of w.shown) parts.push(say(m, true));
  if (w.after.length > 0) {
    parts.push("", "The channel just after it:");
    for (const m of w.after) parts.push(say(m));
  }
  return { text: parts.join("\n"), messages: refs, own };
}

export interface ItemsNew {
  title: string;
  description: string;
  owes: Owes;
  status: ActionItemStatus;
  why: string;
  escalation: boolean;
  /** Message refs (`m3`), every one of them shown. */
  messages: string[];
}

export interface ItemsUpdate {
  /** An item ref (`i2`), one of those listed. */
  item: string;
  status: ActionItemStatus;
  why: string;
  escalation: boolean;
  messages: string[];
}

export interface ItemsAnswer {
  newItems: ItemsNew[];
  updates: ItemsUpdate[];
}

const ITEM_TITLE_MAX = 120;
const ITEM_DESCRIPTION_MAX = 1_000;
const WHY_MAX = 200;

const oneLine = (v: string, max: number): string => {
  const t = v.replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

/**
 * The gate an answer passes before anything is written. A shape that is not an
 * answer at all throws `TriageRefused` (the receipt says `invalid_schema`, as
 * the triage's). Within a good shape, an entry that cites nothing it may is
 * dropped rather than refusing the rest: a new item must come from at least one
 * of the conversation's OWN messages (`own`; never only the channel around a
 * lone message, which is read as its own conversation and would open the same
 * item twice), and an update must name a listed item. Refs it was not shown
 * are dropped; at most `ITEMS_NEW_MAX` new items, one update an item.
 */
export function acceptItems(value: unknown, refs: { messages: ReadonlySet<string>; own: ReadonlySet<string>; items: ReadonlySet<string> }): ItemsAnswer {
  if (value === null || typeof value !== "object" || Array.isArray(value)) throw new TriageRefused("not an object");
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.new_items)) throw new TriageRefused("new_items is not a list");
  if (!Array.isArray(v.updates)) throw new TriageRefused("updates is not a list");
  const cited = (x: unknown): string[] =>
    Array.isArray(x) ? Array.from(new Set(x.filter((r): r is string => typeof r === "string").map((r) => r.trim()).filter((r) => refs.messages.has(r)))) : [];
  const fields = (e: Record<string, unknown>): { status: ActionItemStatus; why: string; escalation: boolean } => {
    if (!isActionItemStatus(e.status)) throw new TriageRefused("status is not a status");
    if (typeof e.escalation !== "boolean") throw new TriageRefused("escalation is not a boolean");
    if (typeof e.why !== "string") throw new TriageRefused("why is not text");
    return { status: e.status, why: oneLine(e.why, WHY_MAX), escalation: e.escalation };
  };
  const entry = (x: unknown, what: string): Record<string, unknown> => {
    if (x === null || typeof x !== "object" || Array.isArray(x)) throw new TriageRefused(`${what} is not an object`);
    return x as Record<string, unknown>;
  };

  const newItems: ItemsNew[] = [];
  for (const x of v.new_items) {
    const e = entry(x, "a new item");
    if (typeof e.title !== "string" || typeof e.description !== "string") throw new TriageRefused("a new item's title or description is not text");
    if (e.owes !== "us" && e.owes !== "them") throw new TriageRefused("owes is not us or them");
    const f = fields(e);
    const title = oneLine(e.title, ITEM_TITLE_MAX);
    const messages = cited(e.messages).filter((r) => refs.own.has(r));
    if (title === "" || messages.length === 0) continue;
    const d = e.description.trim();
    newItems.push({ title, description: d.length > ITEM_DESCRIPTION_MAX ? `${d.slice(0, ITEM_DESCRIPTION_MAX - 1)}…` : d, owes: e.owes, ...f, messages });
  }

  const updates: ItemsUpdate[] = [];
  for (const x of v.updates) {
    const e = entry(x, "an update");
    const f = fields(e);
    const item = typeof e.item === "string" ? e.item.trim() : "";
    if (!refs.items.has(item) || updates.some((u) => u.item === item)) continue;
    updates.push({ item, ...f, messages: cited(e.messages) });
  }
  return { newItems: newItems.slice(0, ITEMS_NEW_MAX), updates };
}
